import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PatrolAdmin from "./PatrolAdmin";
import PatrolFleet from "../components/PatrolFleet";
import { ParkedLocationField, ParkedLocationDialog } from "../components/ParkedLocationPicker";
import { FuelLevelField, FuelLevelDialog } from "../components/FuelLevelField";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Car,
  LogIn,
  LogOut,
  UserPlus,
  ArrowRightLeft,
  Clock,
  MapPin,
  RefreshCw,
  Users,
  Fuel,
  Timer,
  Ban,
  Lock,
  LayoutGrid,
  List,
  Filter,
  X,
  Check
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { formatDistanceToNow } from "date-fns";

export default function Patrol() {
  const { user } = useAuth();
  const [cars, setCars] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCar, setSelectedCar] = useState(null);
  const [unlockingCarId, setUnlockingCarId] = useState(null);
  // Shabbos / Yom Tov window (1 h before candles to 1 h after havdalah, from
  // the server): while open, everyone with Patrol access gets Unlock + Lock on
  // every linked car, logged into it or not.
  const [shabbosWin, setShabbosWin] = useState(null);
  useEffect(() => {
    let alive = true;
    let timer = null;
    const load = () => axios.get(`${API}/patrol/shabbos-window`, { withCredentials: true })
      .then((r) => {
        if (!alive) return;
        setShabbosWin(r.data);
        // Re-check right as it opens or closes, so the buttons come and go on time.
        const next = r.data?.active ? r.data.ends_at : r.data?.next_starts_at;
        const ms = next ? new Date(next).getTime() - Date.now() + 2000 : null;
        clearTimeout(timer);
        if (ms != null && ms > 0 && ms < 6 * 60 * 1000) timer = setTimeout(load, ms);
      })
      .catch(() => {});
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(iv); clearTimeout(timer); };
  }, []);
  const shabbosOpen = !!shabbosWin?.active;
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialog states
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showReserveDialog, setShowReserveDialog] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showFuelPicker, setShowFuelPicker] = useState(false);

  // Form states
  const [selectedUnit, setSelectedUnit] = useState("");
  const [parkedLocation, setParkedLocation] = useState("");
  // How the parked location was chosen — a business or the vehicle's own
  // address carries a point to map; typing carries nothing.
  const [parkedLocationMeta, setParkedLocationMeta] = useState(null);
  const [logoutNote, setLogoutNote] = useState("");
  const [fuelLevel, setFuelLevel] = useState("");
  const [reserveDuration, setReserveDuration] = useState(15);
  const [reserveForUnit, setReserveForUnit] = useState("");

  // Cooldown state (array of per-car cooldowns)
  const [userCooldowns, setUserCooldowns] = useState([]);

  // Countdown timer
  const [, setCountdownTick] = useState(0);

  // View mode: "compact" or "classic"
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("patrolViewMode") || "compact";
  });

  // Car filter (array of car_ids to show, empty = show all)
  const [carFilter, setCarFilter] = useState(() => {
    const saved = localStorage.getItem("patrolCarFilter");
    return saved ? JSON.parse(saved) : [];
  });
  const [showFilterPopover, setShowFilterPopover] = useState(false);

  // Get user's linked unit
  const userUnit = user?.linked_unit;

  // A unit that joined a car mid-patrol is running its own session, so it sees
  // its own elapsed time rather than the car's, which belongs to the unit that
  // logged the car in. Cars joined before joined_sessions existed carry no
  // entry and fall back to the car's start.
  // Joining puts you on someone else's car and starts your own session, so it
  // asks first rather than acting on a single tap.
  const [joinConfirmCar, setJoinConfirmCar] = useState(null);

  const myJoinedStart = (car) =>
    (car.joined_sessions || []).find((s) => s.unit_name === userUnit)?.started_at || null;
  
  // Check if user's unit has patrol status
  const userUnitData = units.find(u => u.name === userUnit);
  const userHasPatrolStatus = userUnitData?.is_patrol === true;

  // Filter patrol units only
  const patrolUnits = units.filter(u => u.is_patrol);

  useEffect(() => {
    fetchData();
    fetchCooldowns();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
      fetchCooldowns();
      checkExpiredReservations();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timer - update every second for real-time display
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdownTick(t => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Clean up filter when cars change (remove deleted cars from filter)
  useEffect(() => {
    if (cars.length > 0 && carFilter.length > 0) {
      const validCarIds = cars.map(c => c.car_id);
      const cleanedFilter = carFilter.filter(id => validCarIds.includes(id));
      if (cleanedFilter.length !== carFilter.length) {
        setCarFilter(cleanedFilter);
        localStorage.setItem("patrolCarFilter", JSON.stringify(cleanedFilter));
      }
    }
  }, [cars]);

  const fetchData = async () => {
    try {
      const [carsRes, unitsRes] = await Promise.all([
        axios.get(`${API}/patrol/cars?patrol_view=true`, { withCredentials: true }),
        axios.get(`${API}/units`, { withCredentials: true })
      ]);
      setCars(carsRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error("Failed to load patrol data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCooldowns = async () => {
    try {
      const response = await axios.get(`${API}/patrol/cooldowns`, { withCredentials: true });
      setUserCooldowns(response.data.cooldowns || []);
    } catch (error) {
      // Ignore cooldown fetch errors
    }
  };

  const checkExpiredReservations = async () => {
    try {
      await axios.post(`${API}/patrol/check-expired-reservations`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore errors
    }
  };

  const refreshCars = async () => {
    try {
      const [response, cooldownRes] = await Promise.all([
        axios.get(`${API}/patrol/cars?patrol_view=true`, { withCredentials: true }),
        axios.get(`${API}/patrol/cooldowns`, { withCredentials: true })
      ]);
      setCars(response.data);
      setUserCooldowns(cooldownRes.data.cooldowns || []);
      toast.success("Refreshed");
    } catch (error) {
      toast.error("Failed to refresh");
    }
  };

  // Login with user's unit only - requires patrol status
  const handleLoginClick = (car) => {
    if (!userUnit) {
      toast.error("Your account is not linked to a unit");
      return;
    }
    if (!userHasPatrolStatus) {
      toast.error("Your unit does not have patrol status");
      return;
    }
    setSelectedCar(car);
    setShowLoginDialog(true);
  };

  const handleLoginConfirm = async () => {
    if (!selectedCar) return;
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/login`,
        { unit_name: userUnit },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowLoginDialog(false);
      toast.success(`${userUnit} logged into ${selectedCar.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to login");
    }
  };

  // Join with user's unit only - requires patrol status
  const handleJoin = async (car) => {
    if (!userUnit) {
      toast.error("Your account is not linked to a unit");
      return;
    }
    if (!userHasPatrolStatus) {
      toast.error("Your unit does not have patrol status");
      return;
    }
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${car.car_id}/join`,
        { unit_name: userUnit },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === car.car_id ? response.data : c));
      toast.success(`${userUnit} joined ${car.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join");
    }
  };

  const handleParkedLocationChange = (text, meta = null) => {
    setParkedLocation(text);
    setParkedLocationMeta(meta || null);
  };

  // Google Maps link for a parked spot that came off the vehicle's own
  // position. A typed-in note is often a landmark rather than an address, so
  // it stays plain text.
  const parkedLocationMapHref = (car) => {
    if (!car.parked_location) return null;
    if (!["business", "address"].includes(car.parked_location_source)) return null;
    if (car.parked_location_lat != null && car.parked_location_lng != null) {
      return `https://www.google.com/maps/search/?api=1&query=${car.parked_location_lat},${car.parked_location_lng}`;
    }
    // No point stored — fall back to the text, minus the cross streets, which
    // Google reads as part of the address and fails on.
    const plain = car.parked_location.replace(/\s*\[[^\]]*\]\s*$/, "");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(plain + ", Brooklyn, NY")}`;
  };

  // Logout - only if user's unit is assigned
  const handleLogout = async () => {
    if (!parkedLocation.trim() || !selectedCar) {
      toast.error("Parked location is required");
      return;
    }
    if (!fuelLevel) {
      toast.error("Please select fuel level");
      return;
    }
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/logout`,
        {
          parked_location: parkedLocation,
          note: logoutNote || null,
          fuel_level: fuelLevel,
          parked_location_source: parkedLocationMeta?.source || null,
          parked_location_lat: parkedLocationMeta?.lat ?? null,
          parked_location_lng: parkedLocationMeta?.lng ?? null,
        },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowLogoutDialog(false);
      setParkedLocation("");
      setParkedLocationMeta(null);
      setLogoutNote("");
      setFuelLevel("");
      toast.success(`Logged out of ${selectedCar.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to logout");
    }
  };

  // Leave - user's unit only
  const handleLeave = async (car) => {
    if (!userUnit) return;
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${car.car_id}/leave`,
        { unit_name: userUnit },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === car.car_id ? response.data : c));
      toast.success(`${userUnit} left ${car.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave");
    }
  };

  // Transfer to another unit
  const handleTransfer = async () => {
    if (!selectedUnit || !selectedCar) return;
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/transfer`,
        { unit_name: selectedUnit },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowTransferDialog(false);
      setSelectedUnit("");
      toast.success(`Assignment transferred to ${selectedUnit}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to transfer");
    }
  };

  // Reserve a car
  const handleReserveClick = (car) => {
    if (!userUnit && !hasPatrolAdminAccess) {
      toast.error("Your account is not linked to a unit");
      return;
    }
    if (!userHasPatrolStatus && !hasPatrolAdminAccess) {
      toast.error("Your unit does not have patrol status");
      return;
    }
    // Check car-specific cooldown
    const carCooldown = getCooldownRemaining(car.car_id);
    if (carCooldown && !hasPatrolAdminAccess) {
      toast.error(`You are on cooldown for this car (${carCooldown} min remaining)`);
      return;
    }
    setSelectedCar(car);
    setReserveDuration(15);
    setReserveForUnit("");
    setShowReserveDialog(true);
  };

  const handleReserveConfirm = async () => {
    if (!selectedCar) return;
    try {
      // In regular patrol view, always reserve for user's own unit (even if admin)
      const payload = {
        duration_minutes: reserveDuration,
        unit_name: userUnit  // Always use user's linked unit in regular patrol view
      };
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/reserve`,
        payload,
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowReserveDialog(false);
      toast.success(`Reserved ${selectedCar.name} for ${reserveDuration} minutes`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reserve");
    }
  };

  const handleCancelReservation = async (car) => {
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${car.car_id}/cancel-reservation`,
        {},
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === car.car_id ? response.data : c));
      // Show cooldown message if applied
      if (response.data.cooldown_message) {
        toast.warning(response.data.cooldown_message);
      } else {
        toast.success("Reservation cancelled");
      }
      // Refresh cooldowns after cancellation
      fetchCooldowns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel reservation");
    }
  };

  // Helper to format countdown time
  const formatCountdown = (expiresAt) => {
    if (!expiresAt) return "";
    const expires = new Date(expiresAt);
    const now = new Date();
    const diff = expires - now;
    if (diff <= 0) return "Expired";
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Check if user's unit has this car reserved
  const isUserReserved = (car) => {
    return car.status === "reserved" && car.reserved_by === userUnit;
  };

  // Get cooldown remaining time for a specific car (also checks "all cars" cooldowns)
  const getCooldownRemaining = (carId) => {
    // Check car-specific cooldown
    let cooldown = userCooldowns.find(c => c.car_id === carId);
    // Also check "all cars" cooldown (car_id is null)
    if (!cooldown) {
      cooldown = userCooldowns.find(c => c.car_id === null);
    }
    if (!cooldown) return null;
    const expires = new Date(cooldown.cooldown_expires_at);
    const now = new Date();
    const diff = expires - now;
    if (diff <= 0) return null;
    const minutes = Math.ceil(diff / 60000);
    return minutes;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "available":
        return <Badge className="bg-green-600">Available</Badge>;
      case "active_patrol":
        return <Badge className="bg-blue-600">Active Patrol</Badge>;
      case "out_of_service":
        return <Badge className="bg-red-600">Out of Service</Badge>;
      case "reserved":
        return <Badge className="bg-amber-600">Reserved</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Filter cars
  const filteredCars = cars.filter(car => {
    // Hide cars marked as hidden_from_patrol (out of service and hidden)
    if (car.hidden_from_patrol) {
      return false;
    }
    // Apply car filter first (if any cars are selected)
    if (carFilter.length > 0 && !carFilter.includes(car.car_id)) {
      return false;
    }
    // Then apply search filter
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      car.name?.toLowerCase().includes(search) ||
      car.assigned_unit?.toLowerCase().includes(search) ||
      car.joined_units?.some(u => u.toLowerCase().includes(search))
    );
  });

  // Toggle car in filter
  const toggleCarFilter = (carId) => {
    const newFilter = carFilter.includes(carId)
      ? carFilter.filter(id => id !== carId)
      : [...carFilter, carId];
    setCarFilter(newFilter);
    localStorage.setItem("patrolCarFilter", JSON.stringify(newFilter));
  };

  // Clear all filters
  const clearCarFilter = () => {
    setCarFilter([]);
    localStorage.setItem("patrolCarFilter", JSON.stringify([]));
  };

  // Check if user's unit is in this car (either primary or joined)
  const isUserInCar = (car) => {
    if (!userUnit) return false;
    return car.assigned_unit === userUnit || car.joined_units?.includes(userUnit);
  };

  // Check if user's unit is the primary assigned unit
  const isUserPrimary = (car) => {
    return userUnit && car.assigned_unit === userUnit;
  };

  // Check if user's unit has joined this car
  const isUserJoined = (car) => {
    return userUnit && car.joined_units?.includes(userUnit);
  };

  // Check if user has patrol admin access (must be explicitly true)
  const hasPatrolAdminAccess = user?.role === 'admin' || user?.patrol_admin_access === true;

  // Fleet (MyGeotab) is its own grant — neither permission implies the other.
  const hasFleetAccess = user?.role === 'admin' || user?.fleet_access === true;

  // Tabs only appear if there's something besides the plain patrol view to show.
  const extraTabCount = (hasPatrolAdminAccess ? 1 : 0) + (hasFleetAccess ? 1 : 0);

  // Send an unlock command to the DroneMobile vehicle linked to a patrol car.
  const handleDroneUnlock = async (car) => {
    setUnlockingCarId(car.car_id);
    try {
      const res = await axios.post(`${API}/patrol/cars/${car.car_id}/unlock-drone`, {}, { withCredentials: true });
      toast.success(`Unlock sent to ${res.data?.vehicle_name || car.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unlock vehicle");
    } finally {
      setUnlockingCarId(null);
    }
  };
  const handleDroneLock = async (car) => {
    setUnlockingCarId(car.car_id);
    try {
      const res = await axios.post(`${API}/patrol/cars/${car.car_id}/lock-drone`, {}, { withCredentials: true });
      toast.success(`Lock sent to ${res.data?.vehicle_name || car.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to lock vehicle");
    } finally {
      setUnlockingCarId(null);
    }
  };

  // Over Shabbos / Yom Tov, a car nobody is in (available, reserved, out of
  // service) gets Unlock / Lock too — under its Login / Reserve. A car with a
  // unit in it already shows them beside its unit (renderDroneUnlock there).
  const renderShabbosDrone = (car) => {
    if (!shabbosOpen || car.assigned_unit) return null;
    const btns = renderDroneUnlock(car);
    return btns ? <div className="flex flex-wrap gap-1 mt-1.5">{btns}</div> : null;
  };

  // Unlock button: for the primary unit currently logged into a car that's
  // linked to a DroneMobile vehicle — or, over Shabbos / Yom Tov, for everyone
  // (with Lock beside it) — and only if the user isn't blocked from patrol unlock.
  const renderDroneUnlock = (car) => {
    if (!car.drone_device_key || user?.patrol_unlock_disabled) return null;
    if (!isUserPrimary(car) && !shabbosOpen) return null;
    const busy = unlockingCarId === car.car_id;
    const btnCls = viewMode === "compact" ? "h-5 text-[9px] px-1.5" : "h-6 text-[10px] px-2";
    return (
      <>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            className={`border-amber-500/60 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 ${viewMode === "compact" ? "h-5 text-[9px] px-1.5" : "h-6 text-[10px] px-2"}`}
          >
            <Lock className="w-2.5 h-2.5 mr-0.5" />{busy ? "…" : "Unlock"}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Unlock {car.drone_vehicle_name || car.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends a remote unlock command to {car.drone_vehicle_name || car.name}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => handleDroneUnlock(car)}
            >
              <Lock className="w-4 h-4 mr-1" />Unlock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {shabbosOpen && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" disabled={busy}
              className={`border-slate-500/60 text-slate-600 dark:text-slate-300 hover:bg-slate-500/10 ${btnCls}`}>
              <Lock className="w-2.5 h-2.5 mr-0.5" />{busy ? "…" : "Lock"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-slate-900 border-slate-800">
            <AlertDialogHeader>
              <AlertDialogTitle>Lock {car.drone_vehicle_name || car.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This sends a remote lock command to {car.drone_vehicle_name || car.name}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-slate-600 hover:bg-slate-700" onClick={() => handleDroneLock(car)}>
                <Lock className="w-4 h-4 mr-1" />Lock
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      </>
    );
  };

  // Toggle view mode
  // One controlled dialog serving the Join button in both layouts — this page
  // renders two different trees depending on which extra tabs the unit has.
  const renderJoinConfirm = () => (
    <AlertDialog open={!!joinConfirmCar} onOpenChange={(open) => !open && setJoinConfirmCar(null)}>
      <AlertDialogContent className="bg-slate-900 border-slate-800">
        <AlertDialogHeader>
          <AlertDialogTitle>Join {joinConfirmCar?.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            {userUnit} joins {joinConfirmCar?.assigned_unit || "the unit on this car"} on{" "}
            {joinConfirmCar?.name}. Your patrol session starts now and runs until you leave
            or {joinConfirmCar?.assigned_unit || "the assigned unit"} logs the car out.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="btn-primary"
            onClick={() => {
              const car = joinConfirmCar;
              setJoinConfirmCar(null);
              if (car) handleJoin(car);
            }}
          >
            <UserPlus className="w-4 h-4 mr-1" />Join
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const toggleViewMode = () => {
    const newMode = viewMode === "compact" ? "classic" : "compact";
    setViewMode(newMode);
    localStorage.setItem("patrolViewMode", newMode);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // If the user has neither extra tab, show the regular patrol view on its own
  if (extraTabCount === 0) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold uppercase tracking-wider">Patrol</h1>
            <p className="text-sm text-slate-400">
              {userUnit ? `Logged in as ${userUnit}` : "No unit linked to your account"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Popover open={showFilterPopover} onOpenChange={setShowFilterPopover}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={`h-8 ${carFilter.length > 0 ? 'border-blue-500 text-blue-400' : ''}`}
                >
                  <Filter className="w-4 h-4 mr-1" />
                  {carFilter.length > 0 ? carFilter.length : "Filter"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2 bg-slate-900 border-slate-700" align="end">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Filter Cars</span>
                  {carFilter.length > 0 && (
                    <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={clearCarFilter}>
                      Clear
                    </Button>
                  )}
                </div>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {cars.map(car => (
                    <div
                      key={car.car_id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-800 ${carFilter.includes(car.car_id) ? 'bg-slate-800' : ''}`}
                      onClick={() => toggleCarFilter(car.car_id)}
                    >
                      <Checkbox
                        checked={carFilter.includes(car.car_id)}
                        className="pointer-events-none"
                      />
                      <span className="text-sm font-mono">{car.name}</span>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleViewMode}
              className="h-8"
            >
              {viewMode === "compact" ? <List className="w-4 h-4 mr-1" /> : <LayoutGrid className="w-4 h-4 mr-1" />}
              {viewMode === "compact" ? "Classic" : "Compact"}
            </Button>
            <Button variant="outline" size="sm" onClick={refreshCars}>
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
          </div>
        </div>

        {!userUnit && (
          <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700">
            <CardContent className="py-4">
              <p className="text-amber-800 dark:text-amber-200 text-sm">
                Your account is not linked to a unit. Ask an admin to link your account to a unit in Settings → Users.
              </p>
            </CardContent>
          </Card>
        )}

        <div className={viewMode === "compact"
          ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3"
          : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        }>
          {filteredCars.length === 0 ? (
            <div className="col-span-full text-center py-12 text-slate-500">
              <Car className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No patrol cars available</p>
            </div>
          ) : (
            filteredCars.map((car) => (
              <Card key={car.car_id} className={`bg-slate-900 border-slate-600 ${isUserInCar(car) ? 'ring-2 ring-blue-500' : ''} ${isUserReserved(car) ? 'ring-2 ring-amber-500' : ''}`}>
                <CardContent className={viewMode === "compact" ? "p-2 space-y-1.5" : "p-3 space-y-2"}>
                  {/* Fuel rides the title line beside the status badge. It's a
                      property of the car, not of whichever state block happens
                      to be showing — it used to sit in two different places
                      depending on status, so it moved as the car did. */}
                  <div className="flex items-center justify-between gap-1">
                    <CardTitle className={viewMode === "compact" ? "text-sm sm:text-base font-mono truncate" : "text-base font-mono truncate"}>{car.name}</CardTitle>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {car.fuel_level && (
                        <Badge variant="outline" className={`${viewMode === "compact" ? "text-[9px] px-1 py-0" : "text-[10px] px-1.5 py-0"} ${
                          car.fuel_level === "Empty" || car.fuel_level === "1/8" ? "border-red-500 text-red-600 dark:text-red-400" :
                          car.fuel_level === "1/4" || car.fuel_level === "3/8" ? "border-amber-500 text-amber-600 dark:text-amber-400" :
                          "border-green-500 text-green-600 dark:text-green-400"
                        }`}>
                          <Fuel className="w-2 h-2 mr-0.5" />{car.fuel_level}
                        </Badge>
                      )}
                      {getStatusBadge(car.status)}
                    </div>
                  </div>

                  {car.status === "active_patrol" && (
                    <>
                      <div className={viewMode === "compact" ? "p-1.5 bg-blue-100 dark:bg-blue-900/20 rounded border border-blue-300 dark:border-blue-800/50" : "p-2 bg-blue-100 dark:bg-blue-900/20 rounded border border-blue-300 dark:border-blue-800/50"}>
                        <div className={viewMode === "compact" ? "flex items-center gap-1 text-blue-700 dark:text-blue-200 text-xs sm:text-sm" : "flex items-center gap-2 text-blue-700 dark:text-blue-200 text-sm"}>
                          <Users className="w-3 h-3 flex-shrink-0" />
                          <span className="font-semibold truncate">{car.assigned_unit}</span>
                          {car.assigned_unit === userUnit && (
                            <Badge className={viewMode === "compact" ? "bg-green-600 text-[9px] px-1 py-0" : "bg-green-600 text-[10px] px-1 py-0"}>You</Badge>
                          )}
                        </div>
                        {car.joined_units?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {car.joined_units.map(unit => (
                              <Badge key={unit} variant="outline" className={viewMode === "compact" ? "text-[9px] px-1 py-0" : "text-[10px] px-1 py-0"}>
                                {unit}{unit === userUnit && " (You)"}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {(myJoinedStart(car) || car.assignment_started_at) && (
                          <div className={viewMode === "compact" ? "flex items-center gap-1 mt-1 text-[9px] text-slate-500 dark:text-slate-400" : "flex items-center gap-1 mt-1 text-[10px] text-slate-500 dark:text-slate-400"}>
                            <Clock className="w-2 h-2" />
                            {myJoinedStart(car) ? "Joined " : ""}
                            {formatDistanceToNow(new Date(myJoinedStart(car) || car.assignment_started_at), { addSuffix: true })}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {renderDroneUnlock(car)}
                        {isUserPrimary(car) && (
                          <>
                            <Button size="sm" variant="outline" className={viewMode === "compact" ? "h-5 text-[9px] px-1.5" : "h-6 text-[10px] px-2"} onClick={() => { setSelectedCar(car); setShowTransferDialog(true); }}>
                              <ArrowRightLeft className="w-2.5 h-2.5 mr-0.5" />Transfer
                            </Button>
                            <Button size="sm" variant="destructive" className={viewMode === "compact" ? "h-5 text-[9px] px-1.5" : "h-6 text-[10px] px-2"} onClick={() => { setSelectedCar(car); setShowLogoutDialog(true); }}>
                              <LogOut className="w-2.5 h-2.5 mr-0.5" />Logout
                            </Button>
                          </>
                        )}
                        {isUserJoined(car) && (
                          <Button size="sm" variant="destructive" className={viewMode === "compact" ? "h-5 text-[9px] px-1.5" : "h-7 text-xs"} onClick={() => handleLeave(car)}>
                            <LogOut className="w-2.5 h-2.5 mr-0.5" />Leave
                          </Button>
                        )}
                        {!isUserInCar(car) && userUnit && userHasPatrolStatus && (
                          <Button size="sm" variant="outline" className={viewMode === "compact" ? "h-5 text-[9px] px-1.5" : "h-7 text-xs"} onClick={() => setJoinConfirmCar(car)}>
                            <UserPlus className="w-2.5 h-2.5 mr-0.5" />Join
                          </Button>
                        )}
                      </div>
                    </>
                  )}

                  {car.status === "available" && (
                    <>
                      {car.parked_location && (
                        <div className={viewMode === "compact" ? "flex items-start gap-1 p-1.5 bg-slate-100 dark:bg-slate-800/50 rounded" : "flex items-start gap-2 p-2 bg-slate-100 dark:bg-slate-800/50 rounded"}>
                          <MapPin className={viewMode === "compact" ? "w-3 h-3 text-slate-500 dark:text-slate-400 mt-0.5 flex-shrink-0" : "w-4 h-4 text-slate-500 dark:text-slate-400 mt-0.5 flex-shrink-0"} />
                          {(() => {
                            // Picked off the vehicle's own position, so it maps.
                            const href = parkedLocationMapHref(car);
                            const classes = viewMode === "compact" ? "text-[10px] sm:text-xs font-medium break-words" : "text-sm font-medium";
                            return href ? (
                              <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`${classes} text-blue-600 dark:text-blue-400 hover:underline`}>{car.parked_location}</a>
                            ) : (
                              <span className={`${classes} text-slate-700 dark:text-slate-300`}>{car.parked_location}</span>
                            );
                          })()}
                        </div>
                      )}
                      {userUnit && userHasPatrolStatus && getCooldownRemaining(car.car_id) && (
                        <div className={viewMode === "compact" ? "p-1.5 bg-red-900/20 rounded border border-red-800/50" : "p-2 bg-red-900/20 rounded border border-red-800/50"}>
                          <div className={viewMode === "compact" ? "flex items-center gap-1 text-red-400 text-[9px]" : "flex items-center gap-2 text-red-400 text-xs"}>
                            <Ban className="w-2.5 h-2.5" />
                            <span>Cooldown: {getCooldownRemaining(car.car_id)}m</span>
                          </div>
                        </div>
                      )}
                      {userUnit && userHasPatrolStatus && (
                        <div className="flex gap-1">
                          <Button className={viewMode === "compact" ? "flex-1 !h-6 !min-h-0 text-[10px] btn-primary" : "flex-1 !h-7 !min-h-0 text-xs btn-primary"} onClick={() => handleLoginClick(car)}>
                            <LogIn className="w-2.5 h-2.5 mr-0.5" />Login
                          </Button>
                          {!getCooldownRemaining(car.car_id) && (
                            <Button variant="outline" className={viewMode === "compact" ? "h-6 text-[10px] px-1.5" : "h-7 text-xs"} onClick={() => handleReserveClick(car)}>
                              <Timer className="w-2.5 h-2.5 mr-0.5" />{viewMode === "classic" && "Reserve"}
                            </Button>
                          )}
                        </div>
                      )}
                      {userUnit && !userHasPatrolStatus && (
                        <p className={viewMode === "compact" ? "text-[9px] text-slate-500 italic" : "text-xs text-slate-500 italic"}>No patrol status</p>
                      )}
                    </>
                  )}

                  {car.status === "reserved" && (
                    <>
                      <div className={`${viewMode === "compact" ? "p-1.5" : "p-2"} rounded border ${isUserReserved(car) ? 'bg-green-100 dark:bg-green-900/20 border-green-300 dark:border-green-800/50' : 'bg-amber-100 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800/50'}`}>
                        <div className={viewMode === "compact" ? "flex items-center gap-1 text-amber-700 dark:text-amber-200 text-xs" : "flex items-center gap-2 text-amber-700 dark:text-amber-200 text-sm"}>
                          <Lock className="w-2.5 h-2.5" />
                          <span className="font-semibold truncate">{car.reserved_by || "Admin"}</span>
                          {isUserReserved(car) && (
                            <Badge className={viewMode === "compact" ? "bg-green-600 text-[9px] px-1 py-0" : "bg-green-600 text-[10px] px-1 py-0"}>You</Badge>
                          )}
                        </div>
                        {car.reservation_expires_at ? (
                          <div className={viewMode === "compact" ? "flex items-center gap-1 mt-1 text-base font-mono font-bold text-amber-600 dark:text-amber-400" : "flex items-center gap-1 mt-1 text-lg font-mono font-bold text-amber-600 dark:text-amber-400"}>
                            <Timer className="w-3 h-3" />
                            {formatCountdown(car.reservation_expires_at)}
                          </div>
                        ) : (
                          <div className={viewMode === "compact" ? "flex items-center gap-1 mt-1 text-[9px] text-amber-600 dark:text-amber-400" : "flex items-center gap-1 mt-1 text-xs text-amber-600 dark:text-amber-400"}>
                            <Lock className="w-2.5 h-2.5" />
                            No Timer
                          </div>
                        )}
                      </div>
                      {isUserReserved(car) && (
                        <div className="flex gap-1">
                          <Button className={viewMode === "compact" ? "flex-1 !h-6 !min-h-0 text-[10px] btn-primary" : "flex-1 !h-7 !min-h-0 text-xs btn-primary"} onClick={() => handleLoginClick(car)}>
                            <LogIn className="w-2.5 h-2.5 mr-0.5" />Login
                          </Button>
                          <Button variant="destructive" className={viewMode === "compact" ? "h-6 text-[10px] px-1.5" : "h-7 text-xs"} onClick={() => handleCancelReservation(car)}>
                            <Ban className="w-2.5 h-2.5 mr-0.5" />{viewMode === "classic" && "Cancel"}
                          </Button>
                        </div>
                      )}
                      {!isUserReserved(car) && (
                        <p className={viewMode === "compact" ? "text-[9px] text-slate-500 italic" : "text-xs text-slate-500 italic"}>Reserved</p>
                      )}
                    </>
                  )}

                  {car.status === "out_of_service" && (
                    <div className={viewMode === "compact" ? "p-1.5 bg-red-100 dark:bg-red-900/20 rounded border border-red-300 dark:border-red-800/50" : "p-2 bg-red-100 dark:bg-red-900/20 rounded border border-red-300 dark:border-red-800/50"}>
                      <p className={viewMode === "compact" ? "text-[9px] text-red-700 dark:text-red-200" : "text-xs text-red-700 dark:text-red-200"}>{car.out_of_service_note}</p>
                    </div>
                  )}
                  {renderShabbosDrone(car)}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Login Confirmation Dialog */}
        <Dialog open={showLoginDialog} onOpenChange={(open) => { setShowLoginDialog(open); }}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader><DialogTitle>Confirm Login to {selectedCar?.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-slate-300">
                You are about to log in as <span className="font-bold text-blue-400">{userUnit}</span> to <span className="font-bold">{selectedCar?.name}</span>.
              </p>
              {selectedCar?.fuel_level && (
                <div className="flex items-center gap-2 p-3 bg-slate-800 rounded">
                  <Fuel className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-300">Current fuel level: <span className="font-medium">{selectedCar.fuel_level}</span></span>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={handleLoginConfirm} className="flex-1 btn-primary">
                  <LogIn className="w-4 h-4 mr-2" />Confirm Login
                </Button>
                <Button variant="outline" onClick={() => setShowLoginDialog(false)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Logout Dialog */}
        <Dialog open={showLogoutDialog} onOpenChange={(open) => { setShowLogoutDialog(open); if (!open) { setParkedLocation(""); setParkedLocationMeta(null); setLogoutNote(""); setFuelLevel(""); setShowLocationPicker(false); setShowFuelPicker(false); } }}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader><DialogTitle>Logout from {selectedCar?.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <ParkedLocationField
                value={parkedLocation}
                onPick={() => setShowLocationPicker(true)}
              />
              <FuelLevelField
                carId={selectedCar?.car_id}
                allowVehicleReading
                value={fuelLevel}
                onChange={setFuelLevel}
                onManual={() => setShowFuelPicker(true)}
              />
              <div>
                <Label>Note (optional)</Label>
                <Input
                  value={logoutNote}
                  onChange={(e) => setLogoutNote(e.target.value)}
                  placeholder="Any notes about this session..."
                  className="input-dark"
                />
              </div>
              <Button onClick={handleLogout} className="w-full btn-primary" disabled={!parkedLocation.trim() || !fuelLevel}>
                <LogOut className="w-4 h-4 mr-2" />Confirm Logout
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {renderJoinConfirm()}

        {/* Location picker — its own popup, a sibling of the logout dialog
            rather than nested inside it. */}
        <ParkedLocationDialog
          open={showLocationPicker}
          onOpenChange={setShowLocationPicker}
          carId={selectedCar?.car_id}
          value={parkedLocation}
          onChange={handleParkedLocationChange}
        />
        <FuelLevelDialog
          open={showFuelPicker}
          onOpenChange={setShowFuelPicker}
          value={fuelLevel}
          onChange={setFuelLevel}
        />

        {/* Transfer Dialog */}
        <Dialog open={showTransferDialog} onOpenChange={(open) => { setShowTransferDialog(open); if (!open) setSelectedUnit(""); }}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader><DialogTitle>Transfer {selectedCar?.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Transfer to Unit</Label>
                <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                  <SelectTrigger className="input-dark">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {patrolUnits
                      .filter(u => u.name !== selectedCar?.assigned_unit && !selectedCar?.joined_units?.includes(u.name))
                      .map(u => (
                        <SelectItem key={u.unit_id} value={u.name}>{u.name}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleTransfer} disabled={!selectedUnit} className="w-full btn-primary">
                <ArrowRightLeft className="w-4 h-4 mr-2" />Confirm Transfer
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reserve Dialog */}
        <Dialog open={showReserveDialog} onOpenChange={(open) => { setShowReserveDialog(open); if (!open) { setReserveDuration(15); setReserveForUnit(""); } }}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader><DialogTitle>Reserve {selectedCar?.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-slate-300 text-sm">
                Reserve this car for your unit. You can login at any time during the reservation.
              </p>
              <div className="p-3 bg-amber-100 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800/50 rounded text-xs text-amber-800 dark:text-amber-200 space-y-1">
                <p><strong>Note:</strong> Please login before the timer expires. If you need more time than offered, contact an admin.</p>
                <p><strong>Cooldown:</strong> If you cancel or let this reservation expire, there will be a 5-minute cooldown before you can reserve again (login is still allowed).</p>
              </div>
              <div>
                <Label>Duration</Label>
                <Select value={reserveDuration.toString()} onValueChange={(v) => setReserveDuration(parseInt(v))}>
                  <SelectTrigger className="input-dark">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleReserveConfirm} className="w-full btn-primary">
                <Timer className="w-4 h-4 mr-2" />Confirm Reservation
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // If user has patrol admin access, show tabs with both views
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h1 className="text-2xl font-bold uppercase tracking-wider">Patrol</h1>
      </div>
      {/* Why every car has Unlock / Lock right now. */}
      {shabbosOpen && (
        <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
          🕯️ Shabbos / Yom Tov — Unlock and Lock are open to everyone
          {shabbosWin?.ends_at ? ` until ${new Date(shabbosWin.ends_at).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", hour: "numeric", minute: "2-digit" })}` : ""}
        </div>
      )}
      <Tabs defaultValue="patrol" className="w-full">
        <TabsList
          className={`grid w-full ${extraTabCount === 2 ? "grid-cols-3 max-w-md" : "grid-cols-2 max-w-xs"}`}
        >
          <TabsTrigger value="patrol">Patrol</TabsTrigger>
          {hasPatrolAdminAccess && <TabsTrigger value="admin">FSD</TabsTrigger>}
          {hasFleetAccess && <TabsTrigger value="fleet">Fleet</TabsTrigger>}
        </TabsList>
        <TabsContent value="patrol" className="mt-4 space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <p className="text-sm text-slate-400">
              {userUnit ? `Logged in as ${userUnit}` : "No unit linked to your account"}
            </p>
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 ${carFilter.length > 0 ? 'border-blue-500 text-blue-400' : ''}`}
                  >
                    <Filter className="w-4 h-4 mr-1" />
                    {carFilter.length > 0 ? carFilter.length : "Filter"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2 bg-slate-900 border-slate-700" align="end">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Filter Cars</span>
                    {carFilter.length > 0 && (
                      <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={clearCarFilter}>
                        Clear
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {cars.map(car => (
                      <div
                        key={car.car_id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-800 ${carFilter.includes(car.car_id) ? 'bg-slate-800' : ''}`}
                        onClick={() => toggleCarFilter(car.car_id)}
                      >
                        <Checkbox
                          checked={carFilter.includes(car.car_id)}
                          className="pointer-events-none"
                        />
                        <span className="text-sm font-mono">{car.name}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleViewMode}
                className="h-8"
              >
                {viewMode === "compact" ? <List className="w-4 h-4 mr-1" /> : <LayoutGrid className="w-4 h-4 mr-1" />}
                {viewMode === "compact" ? "Classic" : "Compact"}
              </Button>
              <Button variant="outline" size="sm" onClick={refreshCars}>
                <RefreshCw className="w-4 h-4 mr-1" />Refresh
              </Button>
            </div>
          </div>
          {!userUnit && (
            <Card className="bg-amber-100 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700">
              <CardContent className="py-4">
                <p className="text-amber-800 dark:text-amber-200 text-sm">
                  Your account is not linked to a unit. Ask an admin to link your account to a unit in Settings → Users.
                </p>
              </CardContent>
            </Card>
          )}
          <div className={viewMode === "compact"
            ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3"
            : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          }>
            {filteredCars.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-500">
                <Car className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No patrol cars available</p>
              </div>
            ) : (
              filteredCars.map((car) => (
                <Card key={car.car_id} className={`bg-slate-900 border-slate-600 ${isUserInCar(car) ? 'ring-2 ring-blue-500' : ''} ${isUserReserved(car) ? 'ring-2 ring-amber-500' : ''}`}>
                  <CardContent className={viewMode === "compact" ? "p-2 space-y-1.5" : "p-3 space-y-2"}>
                    {/* Fuel rides the title line beside the status badge. It's a
                        property of the car, not of whichever state block happens
                        to be showing — it used to sit in two different places
                        depending on status, so it moved as the car did. */}
                    <div className="flex items-center justify-between gap-1">
                      <CardTitle className={viewMode === "compact" ? "text-sm sm:text-base font-mono truncate" : "text-base font-mono truncate"}>{car.name}</CardTitle>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {car.fuel_level && (
                          <Badge variant="outline" className={`${viewMode === "compact" ? "text-[9px] px-1 py-0" : "text-[10px] px-1.5 py-0"} ${
                            car.fuel_level === "Empty" || car.fuel_level === "1/8" ? "border-red-500 text-red-600 dark:text-red-400" :
                            car.fuel_level === "1/4" || car.fuel_level === "3/8" ? "border-amber-500 text-amber-600 dark:text-amber-400" :
                            "border-green-500 text-green-600 dark:text-green-400"
                          }`}>
                            <Fuel className="w-2 h-2 mr-0.5" />{car.fuel_level}
                          </Badge>
                        )}
                        {getStatusBadge(car.status)}
                      </div>
                    </div>
                    {car.status === "active_patrol" && (
                      <>
                        <div className={viewMode === "compact" ? "p-1.5 bg-blue-100 dark:bg-blue-900/20 rounded border border-blue-300 dark:border-blue-800/50" : "p-2 bg-blue-100 dark:bg-blue-900/20 rounded border border-blue-300 dark:border-blue-800/50"}>
                          <div className={viewMode === "compact" ? "flex items-center gap-1 text-blue-700 dark:text-blue-200 text-xs sm:text-sm" : "flex items-center gap-2 text-blue-700 dark:text-blue-200 text-sm"}>
                            <Users className="w-3 h-3 flex-shrink-0" />
                            <span className="font-semibold truncate">{car.assigned_unit}</span>
                            {car.assigned_unit === userUnit && (
                              <Badge className={viewMode === "compact" ? "bg-green-600 text-[9px] px-1 py-0" : "bg-green-600 text-[10px] px-1 py-0"}>You</Badge>
                            )}
                          </div>
                          {car.joined_units?.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {car.joined_units.map(unit => (
                                <Badge key={unit} variant="outline" className={viewMode === "compact" ? "text-[9px] px-1 py-0" : "text-[10px] px-1 py-0"}>
                                  {unit}{unit === userUnit && " (You)"}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {(myJoinedStart(car) || car.assignment_started_at) && (
                            <div className={viewMode === "compact" ? "flex items-center gap-1 mt-1 text-[9px] text-slate-500 dark:text-slate-400" : "flex items-center gap-1 mt-1 text-[10px] text-slate-500 dark:text-slate-400"}>
                              <Clock className="w-2 h-2" />
                              {myJoinedStart(car) ? "Joined " : ""}
                              {formatDistanceToNow(new Date(myJoinedStart(car) || car.assignment_started_at), { addSuffix: true })}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {renderDroneUnlock(car)}
                          {isUserPrimary(car) && (
                            <>
                              <Button size="sm" variant="outline" className={viewMode === "compact" ? "h-5 text-[9px] px-1.5" : "h-6 text-[10px] px-2"} onClick={() => { setSelectedCar(car); setShowTransferDialog(true); }}>
                                <ArrowRightLeft className="w-2.5 h-2.5 mr-0.5" />Transfer
                              </Button>
                              <Button size="sm" variant="destructive" className={viewMode === "compact" ? "h-5 text-[9px] px-1.5" : "h-6 text-[10px] px-2"} onClick={() => { setSelectedCar(car); setShowLogoutDialog(true); }}>
                                <LogOut className="w-2.5 h-2.5 mr-0.5" />Logout
                              </Button>
                            </>
                          )}
                          {isUserJoined(car) && (
                            <Button size="sm" variant="destructive" className={viewMode === "compact" ? "h-5 text-[9px] px-1.5" : "h-7 text-xs"} onClick={() => handleLeave(car)}>
                              <LogOut className="w-2.5 h-2.5 mr-0.5" />Leave
                            </Button>
                          )}
                          {!isUserInCar(car) && userUnit && userHasPatrolStatus && (
                            <Button size="sm" variant="outline" className={viewMode === "compact" ? "h-5 text-[9px] px-1.5" : "h-7 text-xs"} onClick={() => setJoinConfirmCar(car)}>
                              <UserPlus className="w-2.5 h-2.5 mr-0.5" />Join
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                    {car.status === "available" && (
                      <>
                        {car.parked_location && (
                          <div className={viewMode === "compact" ? "flex items-start gap-1 p-1.5 bg-slate-100 dark:bg-slate-800/50 rounded" : "flex items-start gap-2 p-2 bg-slate-100 dark:bg-slate-800/50 rounded"}>
                            <MapPin className={viewMode === "compact" ? "w-3 h-3 text-slate-500 dark:text-slate-400 mt-0.5 flex-shrink-0" : "w-4 h-4 text-slate-500 dark:text-slate-400 mt-0.5 flex-shrink-0"} />
                            {(() => {
                              // Picked off the vehicle's own position, so it maps.
                              const href = parkedLocationMapHref(car);
                              const classes = viewMode === "compact" ? "text-xs font-medium break-words" : "text-sm font-medium";
                              return href ? (
                                <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`${classes} text-blue-600 dark:text-blue-400 hover:underline`}>{car.parked_location}</a>
                              ) : (
                                <span className={`${classes} text-slate-700 dark:text-slate-300`}>{car.parked_location}</span>
                              );
                            })()}
                          </div>
                        )}
                        {/* Cooldown warning */}
                        {userUnit && userHasPatrolStatus && getCooldownRemaining(car.car_id) && (
                          <div className={viewMode === "compact" ? "p-1.5 bg-red-900/20 rounded border border-red-800/50" : "p-2 bg-red-900/20 rounded border border-red-800/50"}>
                            <div className={viewMode === "compact" ? "flex items-center gap-1 text-red-400 text-[9px]" : "flex items-center gap-2 text-red-400 text-xs"}>
                              <Ban className="w-2.5 h-2.5" />
                              <span>Cooldown: {getCooldownRemaining(car.car_id)}m</span>
                            </div>
                          </div>
                        )}
                        {userUnit && userHasPatrolStatus && (
                          <div className="flex gap-1">
                            <Button className={viewMode === "compact" ? "flex-1 !h-6 !min-h-0 text-[10px] btn-primary" : "flex-1 !h-7 !min-h-0 text-xs btn-primary"} onClick={() => handleLoginClick(car)}>
                              <LogIn className="w-3 h-3 mr-0.5" />Login
                            </Button>
                            {!getCooldownRemaining(car.car_id) && (
                              <Button variant="outline" className={viewMode === "compact" ? "h-6 text-[10px]" : "h-7 text-xs"} onClick={() => handleReserveClick(car)}>
                                <Timer className="w-3 h-3 mr-0.5" />{viewMode === "classic" && "Reserve"}
                              </Button>
                            )}
                          </div>
                        )}
                        {userUnit && !userHasPatrolStatus && (
                          <p className={viewMode === "compact" ? "text-[9px] text-slate-500 italic" : "text-xs text-slate-500 italic"}>No patrol status</p>
                        )}
                      </>
                    )}
                    {/* RESERVED - Tabbed view */}
                    {car.status === "reserved" && (
                      <>
                        <div className={`${viewMode === "compact" ? "p-1.5" : "p-2"} rounded border ${isUserReserved(car) ? 'bg-green-100 dark:bg-green-900/20 border-green-300 dark:border-green-800/50' : 'bg-amber-100 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800/50'}`}>
                          <div className={viewMode === "compact" ? "flex items-center gap-1 text-amber-700 dark:text-amber-200 text-xs" : "flex items-center gap-2 text-amber-700 dark:text-amber-200 text-sm"}>
                            <Lock className="w-2.5 h-2.5" />
                            <span className="font-semibold truncate">{car.reserved_by || "Admin Hold"}</span>
                            {isUserReserved(car) && (
                              <Badge className={viewMode === "compact" ? "bg-green-600 text-[9px] px-1 py-0" : "bg-green-600 text-[10px] px-1 py-0"}>You</Badge>
                            )}
                          </div>
                          {car.reservation_expires_at ? (
                            <div className={viewMode === "compact" ? "flex items-center gap-1 mt-0.5 text-sm sm:text-base font-mono font-bold text-amber-600 dark:text-amber-400" : "flex items-center gap-1 mt-1 text-lg font-mono font-bold text-amber-600 dark:text-amber-400"}>
                              <Timer className="w-3 h-3" />
                              {formatCountdown(car.reservation_expires_at)}
                            </div>
                          ) : (
                            <div className={viewMode === "compact" ? "flex items-center gap-1 mt-0.5 text-[9px] text-amber-600 dark:text-amber-400" : "flex items-center gap-1 mt-1 text-xs text-amber-600 dark:text-amber-400"}>
                              <Lock className="w-2.5 h-2.5" />
                              No Timer
                            </div>
                          )}
                        </div>
                        {isUserReserved(car) && (
                          <div className="flex gap-1">
                            <Button className={viewMode === "compact" ? "flex-1 !h-6 !min-h-0 text-[10px] btn-primary" : "flex-1 !h-7 !min-h-0 text-xs btn-primary"} onClick={() => handleLoginClick(car)}>
                              <LogIn className="w-3 h-3 mr-0.5" />Login
                            </Button>
                            <Button variant="destructive" className={viewMode === "compact" ? "h-6 text-[10px]" : "h-7 text-xs"} onClick={() => handleCancelReservation(car)}>
                              <Ban className="w-3 h-3 mr-0.5" />{viewMode === "classic" && "Cancel"}
                            </Button>
                          </div>
                        )}
                        {!isUserReserved(car) && (
                          <p className={viewMode === "compact" ? "text-[9px] text-slate-500 italic" : "text-xs text-slate-500 italic"}>Reserved by another</p>
                        )}
                      </>
                    )}
                    {car.status === "out_of_service" && (
                      <div className={viewMode === "compact" ? "p-1.5 bg-red-100 dark:bg-red-900/20 rounded border border-red-300 dark:border-red-800/50" : "p-2 bg-red-100 dark:bg-red-900/20 rounded border border-red-300 dark:border-red-800/50"}>
                        <p className={viewMode === "compact" ? "text-[9px] text-red-700 dark:text-red-200 line-clamp-2" : "text-xs text-red-700 dark:text-red-200"}>{car.out_of_service_note}</p>
                      </div>
                    )}
                    {renderShabbosDrone(car)}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          {/* Dialogs for tabbed view */}
          <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader><DialogTitle>Confirm Login to {selectedCar?.name}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-slate-300">
                  You are about to log in as <span className="font-bold text-blue-400">{userUnit}</span> to <span className="font-bold">{selectedCar?.name}</span>.
                </p>
                {selectedCar?.fuel_level && (
                  <div className="flex items-center gap-2 p-3 bg-slate-800 rounded">
                    <Fuel className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-300">Current fuel level: <span className="font-medium">{selectedCar.fuel_level}</span></span>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button onClick={handleLoginConfirm} className="flex-1 btn-primary">
                    <LogIn className="w-4 h-4 mr-2" />Confirm Login
                  </Button>
                  <Button variant="outline" onClick={() => setShowLoginDialog(false)}>Cancel</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showLogoutDialog} onOpenChange={(open) => { setShowLogoutDialog(open); if (!open) { setParkedLocation(""); setParkedLocationMeta(null); setLogoutNote(""); setFuelLevel(""); setShowLocationPicker(false); setShowFuelPicker(false); } }}>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader><DialogTitle>Logout from {selectedCar?.name}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <ParkedLocationField
                  value={parkedLocation}
                  onPick={() => setShowLocationPicker(true)}
                />
                <FuelLevelField
                  carId={selectedCar?.car_id}
                  allowVehicleReading
                  value={fuelLevel}
                  onChange={setFuelLevel}
                  onManual={() => setShowFuelPicker(true)}
                />
                <div>
                  <Label>Note (optional)</Label>
                  <Input value={logoutNote} onChange={(e) => setLogoutNote(e.target.value)} placeholder="Any notes about this session..." className="input-dark" />
                </div>
                <Button onClick={handleLogout} disabled={!parkedLocation.trim() || !fuelLevel} className="w-full btn-primary">
                  <LogOut className="w-4 h-4 mr-2" />Confirm Logout
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          {renderJoinConfirm()}

          {/* Location picker — its own popup, a sibling of the logout dialog
            rather than nested inside it. */}
          <ParkedLocationDialog
            open={showLocationPicker}
            onOpenChange={setShowLocationPicker}
            carId={selectedCar?.car_id}
            value={parkedLocation}
            onChange={handleParkedLocationChange}
          />
          <FuelLevelDialog
            open={showFuelPicker}
            onOpenChange={setShowFuelPicker}
            value={fuelLevel}
            onChange={setFuelLevel}
          />
          <Dialog open={showTransferDialog} onOpenChange={(open) => { setShowTransferDialog(open); if (!open) setSelectedUnit(""); }}>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader><DialogTitle>Transfer {selectedCar?.name}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Transfer to Unit</Label>
                  <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                    <SelectTrigger className="input-dark"><SelectValue placeholder="Select unit" /></SelectTrigger>
                    <SelectContent>
                      {patrolUnits.filter(u => u.name !== selectedCar?.assigned_unit && !selectedCar?.joined_units?.includes(u.name)).map(u => (
                        <SelectItem key={u.unit_id} value={u.name}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleTransfer} disabled={!selectedUnit} className="w-full btn-primary">
                  <ArrowRightLeft className="w-4 h-4 mr-2" />Confirm Transfer
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          {/* Reserve Dialog - Tabbed View */}
          <Dialog open={showReserveDialog} onOpenChange={(open) => { setShowReserveDialog(open); if (!open) { setReserveDuration(15); setReserveForUnit(""); } }}>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader><DialogTitle>Reserve {selectedCar?.name}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-slate-300 text-sm">
                  Reserve this car for your unit. You can login at any time during the reservation.
                </p>
                <div className="p-3 bg-amber-100 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800/50 rounded text-xs text-amber-800 dark:text-amber-200 space-y-1">
                  <p><strong>Note:</strong> Please login before the timer expires. If you need more time than offered, contact an admin.</p>
                  <p><strong>Cooldown:</strong> If you cancel or let this reservation expire, there will be a 5-minute cooldown before you can reserve again (login is still allowed).</p>
                </div>
                <div>
                  <Label>Duration</Label>
                  <Select value={reserveDuration.toString()} onValueChange={(v) => setReserveDuration(parseInt(v))}>
                    <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleReserveConfirm} className="w-full btn-primary">
                  <Timer className="w-4 h-4 mr-2" />Confirm Reservation
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
        {hasPatrolAdminAccess && (
          <TabsContent value="admin" className="mt-4">
            <PatrolAdmin embedded />
          </TabsContent>
        )}
        {hasFleetAccess && (
          <TabsContent value="fleet" className="mt-4">
            <PatrolFleet />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
